/**
 * Recompute one deck's study summary from its rows on LOCAL storage — for a
 * deck whose bodies are not (fully) in memory.
 *
 * Two callers, one rule:
 *   - the bridge's stale pass (`store-bridge.ts`): a non-resident deck received
 *     frames, so its bucket is partial and cannot be summarised, but disk holds
 *     the full local set;
 *   - the one-time backfill at hydrate (`flashka-store.ts`): a deck that has no
 *     trusted row yet (the update that introduced summaries, or a version bump)
 *     and is not resident, so the bridge's boot pass will not reach it.
 *
 * The row is written only when the local set is at least `cardCount` rows —
 * the residency yardstick. A deck that was cold when this device first synced
 * has no (or a partial) local set; summarising that would publish counts
 * smaller than the deck. It is left as it was (`'partial'`), and the tile keeps
 * showing its persisted `cardCount`.
 *
 * BOTH CALLERS WALK MANY DECKS, SO BOTH PUBLISH THROUGH A BATCH
 * (`createSummaryPublisher`). Each `applyDeckSummaries` is a store tick, and
 * every tick re-renders whatever lists the decks — Home re-sorts and re-renders
 * its whole grid. One tick per deck is O(decks) renders of an O(decks) screen:
 * measured 2026-09-24 on a 1,536-deck account, the backfill published ~16
 * single-deck ticks a second for as long as the page was open (it re-runs on
 * every launch until it finishes once), and Home was too busy to answer input.
 * The batch makes it one tick per `PUBLISH_MAX_AGE_MS`, whatever the deck count.
 */
import { DECK_STUDY_SUMMARY_TABLE } from '../../sync-engine/framework/local-tables';
import type { DeckId } from '../../sync-engine/schema/shared-types';
import { bucketRowCount, deckBucketComplete } from '../internal/deck-residency';
import { withServerSource } from '../internal/framework/mutation-source';
import { perfNow } from '../internal/perf-log';
import { normalizeDeckCardRows, persistRepairedRows } from '../internal/store/card-rows';
import type { FlashkaState } from '../internal/store/flashka-store';
import {
  applyProgressFloorToBucket,
  deckProgressFloor,
} from '../internal/store/progress-floor';
import { safeBulkPut } from '../internal/store/store-utils';
import type {
  StoredCard,
  StoredDeckSummary,
  SyncStorage,
} from '../provider/sync-storage';
import { DECK_STUDY_SUMMARY_VERSION, summarizeDeckCards } from './summary';

/** `settings` row marking the one-time backfill done for a summary version:
 *  `{ key, value: { version } }`. Written LAST, so an interrupted pass reruns. */
export const DECK_SUMMARY_BACKFILL_SETTINGS_KEY = 'deckSummaryBackfill';

export type DiskRecomputeResult =
  /** The store is not hydrated (reset mid-flight) — nothing done. */
  | 'skipped'
  /** The bucket is complete: the bridge's bucket path owns this deck. */
  | 'resident'
  /** Fewer local rows than `cardCount` — the existing row (if any) is kept. */
  | 'partial'
  /** A fresh row was written to memory and disk — or queued on `publish`. */
  | 'written';

/**
 * What the probe did, plus what it SAW — the caller needs the second half to
 * know whether asking again could possibly answer differently. A `'partial'`
 * deck that is short by 97 rows does not become complete because one more
 * frame landed, and re-reading its whole row set per frame is the per-frame
 * cost the summary layer exists to remove.
 */
export type DiskRecomputeOutcome = {
  result: DiskRecomputeResult;
  /** Rows the probe could account for: this device's disk set ∪ the bucket. */
  rowsSeen: number;
  /** The bucket's own row count at probe time. */
  bucketRows: number;
};

export type DiskRecomputeDeps = {
  store: { getState: () => FlashkaState };
  storage: SyncStorage;
  /**
   * Where a `'written'` row goes. Absent: straight to memory and disk, as one
   * store tick. A caller probing many decks passes a `createSummaryPublisher`
   * batch instead, and `'written'` then means "queued on it".
   */
  publish?: SummaryPublisher;
};

export async function recomputeSummaryFromDisk(
  deckId: string,
  { store, storage, publish }: DiskRecomputeDeps,
): Promise<DiskRecomputeOutcome> {
  // Pinned to the user who asked: a sign-out and the next user's hydrate can
  // both complete during the read (see `deck-disk-load.ts`).
  const userId = store.getState().currentUserId;
  const rows = await storage.getByIndex<StoredCard>('cards', 'deckId', deckId);
  const stillThisUser = () => {
    const s = store.getState();
    return s.isHydrated && s.currentUserId === userId;
  };
  if (!stillThisUser()) return none('skipped');
  if (deckBucketComplete(store.getState(), deckId)) return none('resident');
  // Normalised like a bucket would be (legacy rows may lack `studyState`).
  const { map, repaired } = normalizeDeckCardRows(deckId, rows);
  // Write the healed rows back, as the disk LOADER does: without this every
  // recompute for a deck holding malformed rows repairs them again, and the
  // stored rows stay malformed forever.
  if (repaired.length > 0) await persistRepairedRows(storage, repaired);
  // EVERY await needs the check, not just the first: a sign-out and the next
  // user's hydrate both fit inside the repair write, after which the store and
  // (on web) the database are someone else's. Re-read the state too — the
  // snapshot taken before the await is the previous user's.
  if (!stillThisUser()) return none('skipped');
  const state = store.getState();
  const bucket = state.cards[deckId];
  // Disk rows of a reset deck may predate its floor (the store cleans a row
  // only when it enters memory) — count them as the store would hold them.
  const merged = applyProgressFloorToBucket(
    bucket ? { ...map, ...bucket } : map,
    deckProgressFloor(state, deckId),
  );
  const rowsSeen = bucketRowCount(merged);
  const bucketRows = bucketRowCount(bucket);
  const cardCount = state.deckProperties[deckId]?.cardCount;
  // An UNKNOWN count cannot certify a set as whole — the rule
  // `deckBucketComplete` applies to a bucket, applied here to disk ∪ bucket.
  // The old `?? 0` read unknown as "zero expected", so ANY local row was "the
  // deck": a `sync-diff` carrying 1 of 3 rows for a deck whose meta never got
  // a count (create/fork/copy/import never write one) was persisted as a
  // TRUSTED 1-card summary, and the Home tile said "1 card" until the deck was
  // opened.
  if (cardCount == null || rowsSeen < cardCount) {
    return { result: 'partial', rowsSeen, bucketRows };
  }
  const row: StoredDeckSummary = {
    ...summarizeDeckCards(merged),
    deckId: deckId as DeckId,
    version: DECK_STUDY_SUMMARY_VERSION,
    staleAtISO: null,
  };
  if (publish) {
    publish.add(row, {
      summary: hasOwn(state.deckSummaries, deckId)
        ? state.deckSummaries[deckId]
        : undefined,
      bucket,
    });
  } else {
    withServerSource(() => state.applyDeckSummaries({ [deckId]: row }));
    safeBulkPut(storage, DECK_STUDY_SUMMARY_TABLE, [row]);
  }
  return { result: 'written', rowsSeen, bucketRows };
}

const hasOwn = (o: object, k: string) => Object.prototype.hasOwnProperty.call(o, k);

/** A queued row older than this is published on the next `flushIfDue`. */
export const PUBLISH_MAX_AGE_MS = 500;
/** …and so is a batch this large, however young. */
export const PUBLISH_MAX_ROWS = 200;

/**
 * What a queued row was computed AGAINST: the deck's summary and card bucket
 * at compute time, by identity. If either has moved by the time the batch is
 * published, something newer than the disk read owns the deck — the bridge
 * summarised a now-complete bucket, marked the row stale because frames
 * landed, or the rows the count came from changed — and the queued row would
 * overwrite a fresher answer with an older one. It is dropped; the bridge's
 * own stale tracking re-probes the deck when it can answer again.
 */
export type SummaryBasis = {
  summary: StoredDeckSummary | undefined;
  bucket: FlashkaState['cards'][string] | undefined;
};

export type SummaryPublisher = {
  add: (row: StoredDeckSummary, basis: SummaryBasis) => void;
  /** Publish if the oldest queued row is `maxAgeMs` old or the batch is full. */
  flushIfDue: () => void;
  /** Publish everything queued, as ONE store tick and one bulk write. */
  flush: () => void;
  /** Drop everything queued without publishing. */
  discard: () => void;
  size: () => number;
};

/**
 * A batch for rows computed from disk, published as one store tick.
 *
 * Pinned to the user current at creation: a sign-out or user switch between
 * `add` and `flush` drops the batch, which is the rule every await in
 * `recomputeSummaryFromDisk` already applies to its own write.
 */
export function createSummaryPublisher(
  { store, storage }: Pick<DiskRecomputeDeps, 'store' | 'storage'>,
  {
    maxAgeMs = PUBLISH_MAX_AGE_MS,
    maxRows = PUBLISH_MAX_ROWS,
    now = perfNow,
  }: { maxAgeMs?: number; maxRows?: number; now?: () => number } = {},
): SummaryPublisher {
  const userId = store.getState().currentUserId;
  const queued = new Map<string, { row: StoredDeckSummary; basis: SummaryBasis }>();
  let oldestAt = 0;

  const flush = () => {
    if (queued.size === 0) return;
    const state = store.getState();
    if (!state.isHydrated || state.currentUserId !== userId) {
      queued.clear();
      return;
    }
    const upsert: Record<string, StoredDeckSummary> = {};
    const rows: StoredDeckSummary[] = [];
    for (const [id, { row, basis }] of queued) {
      const summaryNow = hasOwn(state.deckSummaries, id)
        ? state.deckSummaries[id]
        : undefined;
      if (summaryNow !== basis.summary) continue;
      if (state.cards[id] !== basis.bucket) continue;
      if (deckBucketComplete(state, id)) continue; // the bridge's bucket path owns it
      upsert[id] = row;
      rows.push(row);
    }
    queued.clear();
    if (rows.length === 0) return;
    withServerSource(() => state.applyDeckSummaries(upsert));
    safeBulkPut(storage, DECK_STUDY_SUMMARY_TABLE, rows);
  };

  return {
    add: (row, basis) => {
      if (queued.size === 0) oldestAt = now();
      queued.set(row.deckId, { row, basis });
    },
    flushIfDue: () => {
      if (queued.size === 0) return;
      if (queued.size >= maxRows || now() - oldestAt >= maxAgeMs) flush();
    },
    flush,
    discard: () => queued.clear(),
    size: () => queued.size,
  };
}

const none = (result: DiskRecomputeResult): DiskRecomputeOutcome => ({
  result,
  rowsSeen: 0,
  bucketRows: 0,
});

/** Is this persisted row current enough to trust without recomputing? */
export function isTrustedSummaryRow(row: StoredDeckSummary | undefined): boolean {
  return !!row && !row.staleAtISO && row.version === DECK_STUDY_SUMMARY_VERSION;
}
